/**
 * Asks a signed-in person to accept the current Privacy Policy before any
 * screen opens (migration 0065, checklist C-25). Shown by `RequireAuth` when
 * `/auth/me` says this sign-in has not accepted the current version: on first
 * sign-in, and again whenever the policy's effective date moves.
 *
 * The policy opens in a new tab so this step is not lost; it is a public page
 * (`/privacy`), the one this application links everywhere. Signing out is
 * offered beside accepting, because someone who does not accept must have a
 * way to leave that is not a dead end.
 */

import { useState } from 'react';
import { ApiError, api } from '../api.js';
import { useAuth } from '../auth/AuthProvider.js';

export function PrivacyConsent({ version }: { version: string }) {
  const { refresh, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await api.acceptPrivacy(version);
      await refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.message !== caught.code
          ? caught.message
          : 'Could not record that. Try again.',
      );
      setBusy(false);
    }
  }

  return (
    <main className="centered">
      <h1>Privacy Policy</h1>
      <p>
        Before you continue, please read how Techne Books collects, uses and protects information: the{' '}
        <a href="/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        , effective {version}.
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="row">
        <button type="button" disabled={busy} onClick={() => void accept()}>
          {busy ? 'Recording…' : 'Accept and Continue'}
        </button>
        <button type="button" className="link" onClick={() => void signOut()}>
          Sign Out
        </button>
      </div>
    </main>
  );
}
